import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronRight, Target, Layers } from 'lucide-react';
import { useUserProgram } from '@/hooks/useUserProgram';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useProduct, getProductNamespace } from '@/hooks/useProduct';
import { useProductDashboard } from '@/hooks/useProductDashboard';

export function ProgramProgressCard() {
  const navigate = useNavigate();
  const { program, currentPhase, progressPercent, totalActivities, completedActivities, loading, allPrograms } = useUserProgram();
  const { t } = useTranslation();
  const { productId } = useProduct();
  const { programPath } = useProductDashboard();
  const productNs = getProductNamespace(productId);
  
  // Helper for product-specific translations
  const pt = (key: string, fallback: string) => t(`${productNs}:${key}`, fallback);

  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="p-6">
          <Skeleton className="h-6 w-32 mb-4" />
          <Skeleton className="h-32 w-32 mx-auto rounded-full mb-4" />
          <Skeleton className="h-4 w-24 mx-auto" />
        </CardContent>
      </Card>
    );
  }

  // No programs available in the current language - hide the card
  if (allPrograms.length === 0) {
    return null;
  }

  // Programs exist but user hasn't selected one yet
  if (!program) {
    return (
      <Card className="h-full bg-gradient-to-br from-muted/50 to-muted border-muted">
        <CardContent className="p-6 flex flex-col items-center justify-center h-full text-center">
          <Target className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="font-semibold text-lg mb-2">
            {pt('program.noProgress', 'No Progress Yet')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {pt('program.selectProgram', 'Select a program to start tracking.')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const chartData = [
    { name: t('common.programprogresscard.completed', 'Completed'), value: completedActivities, fill: 'hsl(var(--primary))' },
    { name: t('common.programprogresscard.remaining', 'Remaining'), value: totalActivities - completedActivities, fill: 'hsl(var(--muted))' },
  ];

  return (
    <Card className="h-full bg-gradient-to-br from-secondary/10 to-muted/30 border-secondary/20">
      <CardContent className="p-6 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
            {pt('program.yourProgress', 'Your Progress')}
          </h3>
          <Layers className="w-4 h-4 text-muted-foreground" />
        </div>

        {/* Circular Progress */}
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="relative w-32 h-32 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={55}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                  strokeWidth={0}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold">{progressPercent}%</span>
            </div>
          </div>

          {/* Metrics */}
          <div className="text-center mb-4">
            <p className="text-lg font-semibold">
              {completedActivities} / {totalActivities}
            </p>
            <p className="text-sm text-muted-foreground">
              {pt('program.activitiesCompleted', 'activities completed')}
            </p>
          </div>

          {/* Current Phase */}
          {currentPhase && (
            <div className="text-center mb-4 px-4 py-2 bg-background/60 rounded-lg">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                {pt('program.currentPhase', 'Current Phase')}
              </p>
              <p className="font-medium text-sm">{currentPhase.name}</p>
            </div>
          )}
        </div>

        {/* View Details Button */}
        <Button 
          onClick={() => navigate(programPath)} 
          variant="outline"
          className="w-full mt-auto"
        >
          {pt('program.viewFullProgram', 'View Full Program')}
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}
